import {useContext, useEffect, useRef, useState} from 'react';
import {create} from '@bufbuild/protobuf';
import {useConnectClients} from '../grpc/ConnectClientContext';
import {PlayerStateUpdate} from '../../generated/bay_session_pb';
import {StreamPlayerStateRequestSchema} from '../../generated/player_bay_session_pb';
import {StoreContext} from '../components/StoreContextProvider';

// SMG-3389: stale-state watchdog window. If the stream delivers no fresh
// STATE frame within this window — even while heartbeats keep flowing — the
// hook forces a re-snapshot (reconnect) so a deferred-settle or dropped push
// frame self-heals without the manual Single-Shot toggle. This is the BASE
// (first-poll) window: tuned below the ~30s recovery-worker drain so a real
// freeze — which always begins right after a fresh frame, hence at the base
// window — recovers on the first poll or two, and well above the steady-state
// push cadence so an actively-pushing stream (which resets the window on every
// frame) never re-snapshots.
export const STALE_STATE_RESNAPSHOT_MS = 15000;

// SMG-3389: backoff ceiling. Each consecutive re-snapshot that turns up nothing
// new (no sequence advance) doubles the window up to this cap; a real advancing
// frame resets it to the base. This keeps recovery fast for an actual freeze
// (starts at the base window) while collapsing churn on a genuinely idle kiosk
// (no frames to recover) from "every 15s forever" to "settle at one poll/min".
const STALE_STATE_RESNAPSHOT_MAX_MS = 60000;

type Props = { enabled: boolean, retryKey?: number };

// useStreamPlayerState subscribes to the per-user
// evenplay.bay_session.v1.PlayerBaySessionService/StreamPlayerState channel.
// Server-side filtering is by JWT — no station / partner gating is needed and
// the channel is universal (used by every mobile build, FullSwing and
// consumer). since_sequence stays in memory and is reused for reconnects while
// the matching PlayerState projection is still present.
//
// Authorization: the stream MUST send the bay-session-issued player JWT
// (bayPlayerJwt on StoreContext) as the Bearer token. The FullSwing JWT
// is only used once during the FullSwingAuth handshake (in
// StoreContextProvider.fullSwingTokenLogin); thereafter every bay-session
// RPC carries the evenplay-internal JWT. The stream waits for a non-empty
// bayPlayerJwt before opening to avoid the "authz: token is empty" round-
// trip a few seconds before the FullSwingAuth handshake completes.
// A hard reload starts at sequence 0: persisting a cursor without persisting
// its matching state would let the client skip the snapshot needed to rebuild
// currentStationId and the active-bet projections.
const useStreamPlayerState = ({enabled, retryKey = 0}: Props) => {
  const {baySession} = useConnectClients();
  const {bayPlayerJwt} = useContext(StoreContext);
  const [update, setUpdate] = useState<PlayerStateUpdate | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [streamError, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // SMG-3389: stale-state watchdog. Fires a re-snapshot when no fresh STATE
  // frame has arrived within STALE_STATE_RESNAPSHOT_MS, independently of the
  // 5s END/ERROR reconnect loop. Heartbeats do NOT reset it (the deferred-shot
  // freeze happens with heartbeats still flowing), so this is the only timer
  // that recovers an open-but-quiet stream.
  const staleWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // SMG-3389: current watchdog window. Starts at the base, doubles (capped)
  // after each no-op re-snapshot, resets to the base on a real frame.
  const staleBackoffRef = useRef<number>(STALE_STATE_RESNAPSHOT_MS);
  const isMountedRef = useRef(true);
  const enabledRef = useRef(enabled);
  const abortControllerRef = useRef<AbortController | null>(null);
  // In-memory since_sequence used for reconnect and subscriber-side dedupe.
  // It must not survive a hard reload unless the matching PlayerState survives
  // with it.
  const lastSequenceRef = useRef<bigint>(0n);
  // The hook instance can survive a JWT/session change. Tag the retained state
  // with the JWT that produced it so consumers never see the previous user's
  // projection during the render before the new effect opens its stream.
  const updateOwnerJwtRef = useRef('');

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;
    // enabled/JWT changes are session boundaries. Soft reconnects call start()
    // inside this effect and retain the cursor; a new effect must start from a
    // full snapshot and hide the previous stream's projection immediately.
    lastSequenceRef.current = 0n;
    setIsConnected(false);
    setError(null);

    const clearReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const clearStaleWatchdog = () => {
      if (staleWatchdogRef.current) {
        clearTimeout(staleWatchdogRef.current);
        staleWatchdogRef.current = null;
      }
    };

    const cleanup = () => {
      clearReconnect();
      clearStaleWatchdog();
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      if (isMountedRef.current) setIsConnected(false);
    };

    // SMG-3389: reset the backoff to the base window. Called when a real
    // advancing STATE frame lands, so the next quiet period starts polling
    // fast again — a freeze that follows activity recovers at the base window.
    const resetStaleBackoff = () => {
      staleBackoffRef.current = STALE_STATE_RESNAPSHOT_MS;
    };

    // SMG-3389: (re)arm the stale-state watchdog at the CURRENT backoff window.
    // Called when a connection opens and on every fresh STATE frame, so an
    // actively-pushing stream keeps pushing the deadline out and never
    // re-snapshots. When the stream goes quiet of STATE frames (a deferred
    // settle, or a dropped push frame) the deadline elapses and we force a
    // re-snapshot — the same effect the manual Single-Shot toggle achieves
    // today, minus the human. Each fire that recovers nothing doubles the
    // window (capped) so a genuinely idle kiosk backs off instead of
    // re-snapshotting every base window forever.
    const armStaleWatchdog = () => {
      clearStaleWatchdog();

      if (!enabledRef.current) return;

      staleWatchdogRef.current = setTimeout(() => {
        staleWatchdogRef.current = null;
        if (!enabledRef.current || !isMountedRef.current) return;
        // The window elapsed with no advancing frame — widen for the next
        // cycle before re-snapshotting. A real frame will resetStaleBackoff().
        staleBackoffRef.current = Math.min(staleBackoffRef.current * 2, STALE_STATE_RESNAPSHOT_MAX_MS);
        forceResnapshot();
      }, staleBackoffRef.current);
    };

    // SMG-3389: tear down the open (but quiet) connection and reopen it. The
    // request carries lastSequenceRef, while the omitted sinceDbVersion makes
    // the server re-snapshot current state — picking up a settle the open tail
    // never received. isConnected is
    // intentionally left untouched (no flicker): the stream is healthy, we are
    // just refreshing it. start() re-arms the watchdog, giving a bounded poll
    // until a fresh frame finally lands.
    const forceResnapshot = () => {
      if (!enabledRef.current || !isMountedRef.current) return;
      // Distinct breadcrumb: start() below logs the generic reopen line shared
      // with the END/ERROR reconnect path, so without this a watchdog-driven
      // refresh (open-but-quiet stream, expected) is indistinguishable in
      // telemetry from a stream error (a real problem).
      console.log('[useStreamPlayerState] stale-state watchdog fired; forcing re-snapshot');
      abortControllerRef.current?.abort();
      start();
    };

    const scheduleReconnect = () => {
      if (!enabledRef.current) return;
      clearReconnect();
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!enabledRef.current || !isMountedRef.current) return;
        start();
      }, 5000);
    };

    const start = async () => {
      // Gate the stream on a non-empty bayPlayerJwt — without it
      // bay-session returns "authz: token is empty" and our reconnect
      // loop hammers the server in a tight retry. The Main.tsx effect
      // that drives `enabled` already gates on `session` (the
      // partner-service session); this is the corresponding bay-session
      // gate.
      if (!bayPlayerJwt) {
        console.log('[useStreamPlayerState] waiting for bayPlayerJwt before opening stream');
        scheduleReconnect();
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const resumeFrom = lastSequenceRef.current;
      console.log(`[useStreamPlayerState] Starting stream (since=${resumeFrom})`);

      try {
        const request = create(StreamPlayerStateRequestSchema, {sinceSequence: resumeFrom});

        // Mobile intentionally omits sinceDbVersion, so the server sends a
        // fresh DB-backed snapshot before draining the subscribed tail. That
        // first state frame is authoritative even when its broker sequence is
        // equal to the previous cursor (the common idle reconnect case).
        let awaitingAuthoritativeSnapshot = true;

        // bayPlayerJwt is the bay-session-issued evenplay player JWT
        // (from FullSwingAuth). Sent as Authorization: Bearer on every
        // bay-session RPC; the FullSwing JWT is never sent on these
        // calls — it's exchanged once at login for our internal token.
        const iterable = baySession.streamPlayerState(request, {
          signal: controller.signal, headers: {Authorization: `Bearer ${bayPlayerJwt}`}
        });

        // Arm the watchdog as soon as the connection is open: even if no frame
        // (not even a snapshot) ever arrives on this connection, we recover.
        armStaleWatchdog();

        for await (const reply of iterable) {
          if (controller.signal.aborted || !enabledRef.current) break;

          // Heartbeat envelopes carry no sequence advance and no state.
          // Surface them as a connectivity tick (isConnected=true) without
          // updating the dispatcher's `update` so handlers do not re-fire.
          if (reply.heartbeat) {
            if (isMountedRef.current) {
              setIsConnected(true);
              setError(null);
            }
            continue;
          }

          const isAuthoritativeSnapshot = awaitingAuthoritativeSnapshot;
          awaitingAuthoritativeSnapshot = false;

          // The initial snapshot supersedes local state regardless of broker
          // sequence. Dedup applies only to later tail frames; otherwise a
          // snapshot stamped with an unchanged tip is discarded and reload
          // recovery never restores currentStationId.
          if (!isAuthoritativeSnapshot && reply.sequence > 0n && reply.sequence <= lastSequenceRef.current) {
            continue;
          }

          // SMG-3873: "real progress" means the broker sequence strictly
          // advanced past the cursor. The authoritative snapshot a reopened
          // connection always delivers is — on an idle stream — stamped at
          // the unchanged tip; it must be dispatched (that re-hydration is
          // the FSG-8896 fix) but it is neither a reason to reset the
          // SMG-3389 idle backoff nor to move the cursor.
          const advanced = reply.sequence > lastSequenceRef.current;

          if (isAuthoritativeSnapshot) {
            // A soft stream-info failure can stamp the snapshot with 0. Reset
            // the old cursor in that case too, otherwise valid tail frames
            // below the previous connection's cursor would be discarded.
            // SMG-3873: a below-cursor (>0) stamp, though, must not move the
            // cursor backwards — stale tail replays between the stamp and the
            // old cursor would re-fire into consumers and regress derived
            // state. Cursor updates are strictly monotonic apart from the
            // deliberate sequence-0 fallback reset.
            if (reply.sequence <= 0n) {
              lastSequenceRef.current = 0n;
            } else if (advanced) {
              lastSequenceRef.current = reply.sequence;
            }
          } else if (advanced) {
            // advanced implies reply.sequence > cursor >= 0, so a zero-stamped
            // tail frame (soft stream-info failure) never lands here — it is
            // dispatched below without touching the cursor.
            lastSequenceRef.current = reply.sequence;
          }

          if (isMountedRef.current) {
            updateOwnerJwtRef.current = bayPlayerJwt;
            setUpdate(reply);
            setIsConnected(true);
            setError(null);
          }
          // A strictly-advancing STATE frame is real progress — reset the
          // backoff to the base window. A non-advancing authoritative
          // snapshot (the idle-reconnect re-hydration) re-arms at the CURRENT
          // — possibly widened — window instead, so the SMG-3389 idle backoff
          // still settles at the cap. Heartbeats and deduped replays (handled
          // above via `continue`) deliberately fall through without resetting
          // or re-arming.
          if (advanced) {
            resetStaleBackoff();
          }
          armStaleWatchdog();
        }

        if (!controller.signal.aborted && isMountedRef.current) {
          clearStaleWatchdog();
          setIsConnected(false);
          scheduleReconnect();
        }

      } catch (e: any) {
        if (controller.signal.aborted) return;

        clearStaleWatchdog();
        if (isMountedRef.current) {
          setIsConnected(false);
          setError(e?.message || 'Stream error');
        }
        scheduleReconnect();
      }
    };

    if (enabled) {
      start();
    } else {
      cleanup();
    }

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
    // bayPlayerJwt is in the dep list so the stream restarts once the
    // FullSwingAuth handshake lands the JWT (the initial pre-auth
    // open is gated on bayPlayerJwt being non-empty and scheduleReconnect-
    // throttled until then). Re-runs on logout (jwt → '') tear the
    // stream down cleanly via cleanup().
  }, [bayPlayerJwt, baySession, enabled, retryKey]);

  const currentUpdate = enabled && updateOwnerJwtRef.current === bayPlayerJwt ? update : null;
  return {update: currentUpdate, isConnected, streamError};
};

export default useStreamPlayerState;
